import _ from 'lodash';
import moment from 'moment';
import Decimal from 'decimal.js';
import Globalize from './wrappers/globalize';
import { dangerouslyGetState } from '../app/configureStore';
import { truncateNumber } from '@xanadu/ui-shared/utils/numbers';
import { CURRENCY_SHORT_SYMBOLS } from '../app/constants';
import MAIN_ODDS_LADDER from './odds_ladder';

// TODO: Find out what currency and language values the API is going to expect/return.
const LANGUAGE_NAME_CULTURE_MAPPING = {
  'Chinese Simplified': 'zh-Hans',
  'Chinese Traditional': 'zh-Hant',
  English: 'en'
};

// const SUPPORTED_LANGUAGES = [
//   'en', // English
//   'zh-Hant', // Traditional Chinese
//   'zh-Hans' // Simplified Chinese
// ];

const CURRENCY_SYMBOLS = {
  AUD: 'A$',
  BRL: 'R$',
  CAD: 'CA$',
  CNY: 'CN¥',
  EUR: '€',
  GBP: '£',
  HKD: 'HK$',
  IDR: 'IDR',
  JPY: '¥',
  KRW: '₩',
  TWD: 'NT$',
  USD: '$',
};

const LANGUAGE_PARAM_MAPPING = {
  'en-ie': 'en',
  'zh-hans': 'zh_HANS',
  'zh-hant': 'zh_HANT'
};

const getExchangeType = () => dangerouslyGetState().getIn(['user', 'exchangeType']);
const getOddsType = () => dangerouslyGetState().getIn(['user', 'oddsType']);
const getCurrency = () => dangerouslyGetState().getIn(['user', 'currency']);

const paramForLanguage = (lang = 'en') =>
  LANGUAGE_PARAM_MAPPING[lang.toString().replace('_', '-').toLowerCase()] || 'en';

const back = exchangeType => (exchangeType === 'binary' ? 'win' : 'back');

let console = {
  debug() { },
  dir() { },
  error() { },
  group() { },
  groupCollapsed() { },
  groupEnd() { },
  info() { },
  log() { },
  trace() { },
  warn() { }
};

if (!__PRODUCTION__) {
  console = window.console;
}


const cultureForLanguageName = languageName => LANGUAGE_NAME_CULTURE_MAPPING[languageName] || null;

const lay = exchangeType => (exchangeType === 'binary' ? 'lose' : 'lay');

const urlFor = (path, options) => `${options && options.mock ? '/mock' : '/bpapi/rest'}/${path.replace(/^\/|\/$/g, '')}/`;

// NOTE: checking if LocalStorage is available i.e. cookies aren't blocked etc.


const localise = {};

localise.currency = (number = 0, options = {}) => {
  // Globalize requires a Number
  let int = parseFloat(number);

  // Sometimes some APIs return very small negative numbers which leads to this outputting `-$.00`
  if (int < 0.0001 && int > -0.0001) {
    int = 0;
  }

  let addK;
  let addM;
  let abbrev = '';

  const userCurrency = getCurrency();
  _.defaults(options, {
    currency: userCurrency,
    forceFullNumber: false, // NOTE: This is especially for Korean Wons
    // FIXME: fix me with https://github.com/jquery/globalize/issues/479
    form: 'narrow',
    showSymbol: (options.currency || userCurrency) !== 'KRW'
  });


  if (options.currency === 'KRW') {
    addK = (Math.abs(int) > 99999 && Math.abs(int) < 100000000);
    addM = (Math.abs(int) > 99999999);

    if (!options.forceFullNumber) {
      if (addK) {
        int /= 1000;
        abbrev = 'K';
      }
      if (addM) {
        int /= 1000000;
        abbrev = 'M';
      }
    }
  }

  const dropSymbol = (options.showSymbol === false);
  const narrowSymbol = options.form === 'narrow';

  const obj = _.pick(options, [
    'style',
    'currency',
    'minimumIntegerDigits',
    'minimumFractionDigits',
    'maximumFractionDigits',
    'minimumSignificantDigits',
    'maximumSignificantDigits',
    'round',
    'useGrouping'
  ]);

  const output = Globalize.formatCurrency(int, obj.currency, _.omit(obj, 'currency')) + abbrev;

  if (dropSymbol) {
    return output.replace(CURRENCY_SYMBOLS[obj.currency], '');
  } else if (narrowSymbol) {
    return output.replace(CURRENCY_SYMBOLS[obj.currency], CURRENCY_SHORT_SYMBOLS[obj.currency]);
  }
  return output;
};

localise.currencySymbol = (currency = false) => {
  if (!_.isString(currency)) {
    return '';
  }

  return CURRENCY_SHORT_SYMBOLS[currency] || '';
};

localise.date = (date = '', options = {}) => {
  // date = _.isString(date) ? new Date(date.replace('.000+0000', '')) : date;

  const timeObj = moment(date);
  if (!timeObj.isValid()) { console.warn('📖 Invalid moment created', date); }

  const opts = _.merge({
    format: 'd'
  }, options);
  return timeObj.format(opts.format);
};

localise.number = (number, options) => {
  const int = parseFloat(number);

  const obj = _.pick(options, [
    'style',
    'minimumIntegerDigits',
    'minimumFractionDigits',
    'maximumFractionDigits',
    'minimumSignificantDigits',
    'maximumSignificantDigits',
    'round',
    'useGrouping'
  ]);

  return Globalize.formatNumber(int, obj);
};

localise.odds = (odds, options) => {
  const opts = _.extend({
    'exchange-type': getExchangeType(),
    'odds-type': getOddsType(),
    maximumFractionDigits: 3,
    side: back(),
    useGrouping: false
  }, options);

  if (opts['odds-type'].toLowerCase() === 'us') {
    opts.maximumFractionDigits = 5;
  }

  if (opts['odds-type'].toLowerCase() === 'decimal' && odds < 1.01) {
    opts.maximumFractionDigits = 5;
  }

  opts.round = /back-lay/i.test(opts['exchange-type']) && /lay/i.test(opts.side) ? 'ceil' : 'floor';

  const localisedNumber = localise.number(odds, opts);

  if (opts['odds-type'].toLowerCase() === 'us') {
    return parseFloat(truncateNumber(localisedNumber, 2));
  }

  return localisedNumber;
};

/* eslint-disable array-bracket-spacing, no-multi-spaces */
let VALID_ODDS = _.unzip(MAIN_ODDS_LADDER);
/* eslint-enable vars-on-top, array-bracket-spacing, no-multi-spaces */

VALID_ODDS = {
  // '%': VALID_ODDS[4],
  decimal: VALID_ODDS[0],
  // FIXME: Stick with wheichever string edge uses
  hk: VALID_ODDS[1],
  hong_kong: VALID_ODDS[1],
  // indo: VALID_ODDS[2],
  // indonesian: VALID_ODDS[2],
  malay: VALID_ODDS[3],
  // probability: VALID_ODDS[4],
  us: VALID_ODDS[5]
};

const oddsConversion = (value, from, to) => {
  if (isNaN(parseFloat(value)) || String(value).endsWith('.')) return undefined;
  const parsedValue = new Decimal(value);
  const index = VALID_ODDS[from.toLowerCase()].findIndex(v => parsedValue.eq(v));

  return index < 0 ? undefined : VALID_ODDS[to.toLowerCase()][index];
};


function OddsLadder(name, allowed, options) {
  // NOTE: Doing this and not looking it up etc makes this code much more compartmentalised.
  this.name = name;
  this.allowed = allowed;
  this.options = (options || {});
}

_.extend(OddsLadder.prototype, {
  _quantise(odds, direction, strict) {
    const dir = direction.toLowerCase();
    if (isNaN(parseFloat(odds)) || String(odds).endsWith('.')) {
      return this.allowed[dir === 'up' ? this.allowed.length - 1 : 0];
    }
    const parsedOdds = new Decimal(odds);
    if (parsedOdds.gt(this.allowed[this.allowed.length - 1])) {
      return this.allowed[this.allowed.length - 1];
    } else if (parsedOdds.lt(this.allowed[0])) {
      return this.allowed[0];
    }

    let index = this.allowed.findIndex(v => parsedOdds.lte(v));

    if (index === -1) index = this.allowed.length;

    // Snap to nearest ladder entry when input is a backend-rounded value
    // (e.g., backend returns -294.11765 for ladder value -294.117647058823530)
    // Use epsilon comparison to avoid toFixed rounding mismatches
    const epsilon = new Decimal('0.0001');
    let isMatch = parsedOdds.eq(this.allowed[index]);
    if (!isMatch) {
      if (index < this.allowed.length
        && parsedOdds.minus(this.allowed[index]).abs().lt(epsilon)) {
        isMatch = true;
      } else if (index > 0 && parsedOdds.minus(this.allowed[index - 1]).abs().lt(epsilon)) {
        index -= 1;
        isMatch = true;
      }
    }

    return this.allowed[
      Math.min(
        Math.max(
          (index - ((!isMatch) * (dir === 'down'))) + (((2 * (dir === 'up')) - 1) * strict * isMatch),
          0
        ),
        this.allowed.length - 1
      )
    ];
  },
  quantise(odds, exchangeType, isLay) {
    return (/^back-lay$/i.test(exchangeType) && isLay)
      ? this.quantiseDown(odds)
      : this.quantiseUp(odds);
  },
  // If +strict+ is truthy, force reduction even if +odds+ is already a valid quantisation point.`
  // False by default.
  quantiseDown(odds, strict = false) {
    /* eslint-disable no-underscore-dangle */
    if (this.name === 'malay' && !isNaN(parseFloat(odds)) && !String(odds).endsWith('.') && new Decimal(odds).eq(-0.9901)) {
      return this._quantise(1.00001, 'down', strict);
    }

    return this._quantise(odds, 'down', strict);
    /* eslint-enable no-underscore-dangle */
  },
  // If +strict+ is truthy, force increase even if +odds+ is already a valid quantisation point.`
  // False by default.
  quantiseUp(odds, strict = false) {
    /* eslint-disable no-underscore-dangle */
    if (strict && this.name === 'malay' && !isNaN(parseFloat(odds)) && !String(odds).endsWith('.') && new Decimal(odds).eq(1)) {
      return this._quantise(-1.00001, 'up', strict);
    }

    return this._quantise(odds, 'up', strict);
    /* eslint-enable no-underscore-dangle */
  }
});

const ODDS_LADDERS = {
  decimal: new OddsLadder('decimal', VALID_ODDS.decimal),
  hong_kong: new OddsLadder('hong_kong', VALID_ODDS.hong_kong),
  // indonesian: new OddsLadder('indonesian', VALID_ODDS.indonesian),
  malay: new OddsLadder('malay', [...VALID_ODDS.malay].sort((a, b) => new Decimal(a).cmp(b))),
  // probability: new OddsLadder('probability', _.sortBy(VALID_ODDS.probability)),
  us: new OddsLadder('us', VALID_ODDS.us)
};

// HACK: How do we still have 2 sets of odds types depending on the API we're using?
// ODDS_LADDERS['%']    = ODDS_LADDERS['probability'];
ODDS_LADDERS.hk = ODDS_LADDERS.hong_kong;
// ODDS_LADDERS['indo'] = ODDS_LADDERS['indonesian'];

window.ladder = ODDS_LADDERS.malay;

const oddsLadder = oddsType => ODDS_LADDERS[oddsType.toLowerCase()];

// Snap a backend-rounded odds value to its full-precision ladder value.
// The backend returns odds rounded to ~5dp; this finds the matching ladder entry.
// Use epsilon comparison to avoid toFixed rounding mismatches.
const snapToLadder = (odds, oddsType) => {
  if (isNaN(parseFloat(odds)) || String(odds).endsWith('.')) return odds;
  const ladder = ODDS_LADDERS[oddsType.toLowerCase()];
  if (!ladder) return odds;
  const epsilon = new Decimal('0.0001');
  const parsedOdds = new Decimal(odds);
  const match = ladder.allowed.find(v => parsedOdds.minus(v).abs().lt(epsilon));
  return match || odds;
};

/*eslint-disable */
// Disabling eslint because it's more legible to sort them by their value
var metatagOrderMapping = {
  SPORT: 1,
  COUNTRY: 2,
  LOCATION: 3,
  COMPETITION: 4,
  DATE: 5,
  OTHER: 6,
  Root: 7
};
/* eslint-enable */

const platformMap = {
  bpapi: 'BPAPI',
  edge: 'Edge'
};


const helpers = {
  back,
  console,
  cultureForLanguageName,
  lay,
  localise,
  metatagOrderMapping,
  oddsConversion,
  oddsLadder,
  paramForLanguage,
  platformMap,
  snapToLadder,
  urlFor,
};

export default helpers;
